import { useContext, useState } from "react";
import { t } from "i18next";
import { HiOutlinePlus } from "react-icons/hi2";
import { BsStars } from "react-icons/bs";
import { Button, Dropdown } from "components/ui";
import capitalize from "components/ui/utils/capitalize";
import { AIContext } from "contexts/AIContext";
import usePostGenerator from "utils/hooks/usePostGenerator";
import usePostValidator, { getNetworkValidationIndex } from "utils/hooks/usePostValidator";
import MediaProductTypeDropdown from "components/posts_previews/components/MediaProductTypeDropdown";
import { socialNetworksMediaProductTypes } from "views/calendar/SocialNetworkSidePanel";
import ComposerField from "./ComposerField";
import ComposerPostFields from "./ComposerPostFields";

const THREAD_NETWORKS = ["twitter", "threads", "bluesky"];

/**
 * Left pane of the composer: the structured form for the active network's
 * post(s). Thread networks and stories render one fields-block per item,
 * plus an "add" action; everything writes through PostArticle's handlers so
 * the readonly preview on the right updates live.
 */
const ComposerForm = ({
    posts,
    postGroup,
    activeSocialNetwork,
    isEditable = true,
    onTextEdit,
    onTitleEdit,
    onLinkEdit,
    onAssetNew,
    onAssetDelete,
    onAssetEdit,
    onCoverEdit,
    onPreferencesEdit,
    onCommentsToCreateEdit,
    onCreateNetworkPost,
    onDeleteIndexPost
}) => {

    const network = activeSocialNetwork;
    const { getByQueryChat } = useContext(AIContext);
    const { generateEmptyPost, generatePostWithNewMediaProductType } = usePostGenerator();
    const { validationConstants } = usePostValidator();
    const [isGeneratingItem, setIsGeneratingItem] = useState(false);

    const networkPosts = posts?.filter(p => p?.network === network) || [];
    const firstPost = networkPosts[0];
    const mediaProductType = firstPost?.preferences?.media_product_type || "FEED";
    const isStoryMode = mediaProductType == "STORY";

    const allowedMediaProductTypes = socialNetworksMediaProductTypes?.[network] || ["FEED"];
    const isThreadNetwork = THREAD_NETWORKS.includes(network);
    const canAddItem = isEditable && (isThreadNetwork || isStoryMode);
    const itemNoun = network === "twitter"
        ? t('composer.itemTweet', 'Tweet')
        : isStoryMode
            ? t('composer.itemStory', 'Story')
            : t('composer.itemPost', 'Post');

    const handleMediaProductTypeChange = (newMediaProductType) => {
        if (newMediaProductType?.toLowerCase() == mediaProductType?.toLowerCase()) return;
        // Regenerates the network's post for the new format and replaces the
        // previous one(s) — same semantics as the preview header dropdown.
        const newPost = generatePostWithNewMediaProductType(firstPost, network, newMediaProductType);
        onCreateNetworkPost({ ...newPost }, true);
    };

    const handleAddBlankItem = () => {
        const newPost = generateEmptyPost(network, isStoryMode ? "STORY" : undefined);
        onCreateNetworkPost({ ...newPost?.posts?.[0] });
    };

    const handleAddAIItem = async () => {
        setIsGeneratingItem(true);
        try {
            const newPost = generateEmptyPost(network);
            const maxLength = validationConstants?.[getNetworkValidationIndex(network, mediaProductType)]?.description?.max_length;
            const prompt = `You are an AI designed to help write ${capitalize(network)} threads. You will be given the posts of an ongoing thread, and your task is to write the next post in the series. The new post must continue the topic, maintain the same tone and style as the previous posts${maxLength ? `, and fit within the ${capitalize(network)} character limit (${maxLength} characters or less)` : ``}. Here's the process:
                Read the last post of the thread to understand the context.
                Maintain the same tone (whether formal, casual, humorous, etc.) and the same writing style.
                The new post should seamlessly connect to the previous one, keeping the thread coherent and engaging.
                Ensure that the post provides valuable information or insight that moves the topic forward.
                ${networkPosts?.map((post, i) => `Post ${i + 1}: ${post.text}`).join("\n")}
                New post:`;
            const response = await getByQueryChat(prompt);
            onCreateNetworkPost({ ...newPost?.posts?.[0], description: response });
        } catch (err) {
            console.error(err);
        }
        setIsGeneratingItem(false);
    };

    if (!networkPosts?.length) return <></>;

    return (
        <div className="flex flex-col gap-6">
            {allowedMediaProductTypes?.length > 1 && (
                <ComposerField label={t('composer.formatLabel', 'Format')}>
                    <div className="rounded-lg ring-1 ring-gray-300 dark:ring-gray-600 overflow-hidden bg-secondary-500 dark:bg-gray-800 [zoom:0.9]">
                        <MediaProductTypeDropdown
                            key={network}
                            post={firstPost}
                            isEditable={isEditable}
                            allowedMediaProductTypes={allowedMediaProductTypes}
                            onChange={handleMediaProductTypeChange}
                            portal={true}
                        />
                    </div>
                </ComposerField>
            )}

            {networkPosts.map((post, index) => (
                <div
                    key={`${network}-${post?.postIndex}`}
                    className={index > 0 ? "border-t border-gray-100 dark:border-gray-700/60 pt-6" : ""}
                >
                    <ComposerPostFields
                        post={post}
                        network={network}
                        isEditable={isEditable}
                        index={index}
                        count={networkPosts.length}
                        itemNoun={itemNoun}
                        allPosts={posts}
                        onTextEdit={onTextEdit}
                        onTitleEdit={onTitleEdit}
                        onLinkEdit={onLinkEdit}
                        onAssetNew={onAssetNew}
                        onAssetDelete={onAssetDelete}
                        onAssetEdit={onAssetEdit}
                        onCoverEdit={onCoverEdit}
                        onPreferencesEdit={onPreferencesEdit}
                        onCommentsToCreateEdit={onCommentsToCreateEdit}
                        onDeleteIndexPost={onDeleteIndexPost}
                    />
                </div>
            ))}

            {canAddItem && (
                <div className="flex">
                    {isThreadNetwork
                        ? <Dropdown
                            placement="bottom-start"
                            parentClassName="!shrink-0"
                            portal={true}
                            renderTitle={
                                <Button
                                    size="sm"
                                    variant="twoTone"
                                    className="!rounded-xl"
                                    icon={<HiOutlinePlus />}
                                    loading={isGeneratingItem}
                                >
                                    {t('composer.addItem', { defaultValue: 'Add {{item}}', item: itemNoun.toLowerCase() })}
                                </Button>
                            }
                        >
                            <Dropdown.Item onClick={handleAddBlankItem} className="flex items-center gap-2 py-2 px-4">
                                <HiOutlinePlus className="text-lg" />
                                <span>{t('composer.addBlankItem', 'Start from scratch')}</span>
                            </Dropdown.Item>
                            <Dropdown.Item onClick={handleAddAIItem} className="flex items-center gap-2 py-2 px-4">
                                <BsStars className="text-lg text-fuchsia-500" />
                                <span>{t('composer.addAIItem', 'Generate next with AI')}</span>
                            </Dropdown.Item>
                        </Dropdown>
                        : <Button
                            size="sm"
                            variant="twoTone"
                            className="!rounded-xl"
                            icon={<HiOutlinePlus />}
                            onClick={handleAddBlankItem}
                        >
                            {t('composer.addItem', { defaultValue: 'Add {{item}}', item: itemNoun.toLowerCase() })}
                        </Button>
                    }
                </div>
            )}

        </div>
    );
};

export default ComposerForm;
